import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { StateAccessCheckService } from '@fi-shared/fi-shared-services/state-access-check.service';
import { StatisticService } from '@fi-shared/statistic/statistic.service';
import { ObjectTypes } from '@fi-shared/types/object-type';
import { ModuleType } from '@fi-shared/types/module-type';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { DocumentService } from '@fi-app/document/document.service';
import { TagModel } from '@fi-shared/models';
import { AppTextService } from '@fi-shared/app-text';
import { IItemsListParams } from '@fi-app/components/items-list-container-base';
import { ListViewSettingsService } from '@fi-app/utilities/fi-services/list-view-settings.service';
import { ListViewSettingModel } from '@fi-app/components/items-list/list-view-setting.model';
import { ChecklistListDataService } from '@fi-app/checklist/list/checklist-list-data.service';
import { ChecklistListDataModel } from '@fi-app/checklist/list/checklist-list-data.model';
import { IChecklistListStateModel } from '@fi-app/checklist/checklist';
import { BehaviorSubject } from 'rxjs';
import { ListData } from '@fi-app/components/items-list/list-data.service';
import { SideListOptionType } from '@fi-shared/types';
import { EditTabsModel } from '@fi-app/utilities/edit-tabs/edit-tabs.model';
import { MoreMenuMobileSettingsModel } from '@fi-app/utilities/more-menu-mobile/more-menu-mobile-settings.model';
import { DetectDevicesService } from '@fi-shared/fi-shared-services';
import { FiStateService } from '@fi-shared/fi-state-proxy/fi-state.proxy.service';
import { FiDisplayType } from '@fi-shared/types/display-type';

interface ITagTab {
    Label: string;
    ModuleType: ModuleType;
}

@Component({
    selector: 'fi-tag-view',
    templateUrl: './tag-view.component.html',
    styleUrls: ['./tag-view.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-tag-view' },
    standalone: false,
})
export class TagViewComponent implements OnInit {
    @Input('tag') Tag: TagModel;
    HasEditAccess = false;
    checklistListData: ChecklistListDataModel;

    private listViewSettingModels: ListViewSettingModel[] = [];

    readonly FiStateNames = FiStateNames;
    readonly additionalTabs: ITagTab[] = [];
    readonly ModuleType = ModuleType;
    readonly ObjectTypes = ObjectTypes;
    readonly listParams: IItemsListParams = {
        ShowCheckBox: false,
        initialSideListOptionType: SideListOptionType.WithoutAccessCheck,
        showDelete: false,
    };
    readonly checklistListStateModel: IChecklistListStateModel = {
        listData$: new BehaviorSubject<ListData<any>>(null),
    };
    EditTabs: EditTabsModel;
    MoreMenuItems: MoreMenuMobileSettingsModel;

    constructor(
        private stateAccessCheckService: StateAccessCheckService,
        private statisticService: StatisticService,
        private documentService: DocumentService,
        private appTextService: AppTextService,
        private listViewSettings: ListViewSettingsService,
        private checklistListDataService: ChecklistListDataService,
        private cdr: ChangeDetectorRef,
        private detectDevicesService: DetectDevicesService,
        private stateService: FiStateService,
    ) {}

    ngOnInit() {
        this.statisticService.Visit(ModuleType.Tag, ObjectTypes.Tag, this.Tag.Id);
        this.setPageHeaderData();
        this.setListViewSettings();
    }

    isMobilePhone(): boolean {
        return this.detectDevicesService.isMobilePhone();
    }

    getListViewSetting(objectType: ObjectTypes): ListViewSettingModel {
        return this.listViewSettingModels.find((model: ListViewSettingModel) => model.ObjectType === objectType);
    }

    editTag() {
        this.stateService.go(FiStateNames.Tag.Edit, { Id: this.Tag.Id });
    }

    private setPageHeaderData() {
        this.HasEditAccess = this.stateAccessCheckService.isStateAllowedForCurrentUser(FiStateNames.Tag.Edit);
        this.EditTabs = new EditTabsModel(this.Tag.Name, this.appTextService.get('back'));
        this.MoreMenuItems = new MoreMenuMobileSettingsModel();

        if (this.HasEditAccess) {
            this.MoreMenuItems.AddListItem(this.appTextService.get('common.edit'), () => this.editTag());
        }
    }

    private setListViewSettings() {
        this.listViewSettings
            .getSettings([ObjectTypes.User, ObjectTypes.Document, ObjectTypes.MediaBank, ObjectTypes.CheckList])
            .then((result: ListViewSettingModel[]) => {
                this.listViewSettingModels = result;
                this.setTagViewTabs();
            });
    }

    private setTagViewTabs() {
        this.Tag.ModuleTypes.forEach((moduleType: number) => {
            switch (moduleType) {
                case ModuleType.Document:
                    this.setTaggedItemsTab('common.pages', ModuleType.Document);
                    break;
                case ModuleType.CheckList:
                    this.checklistListData = this.checklistListDataService.getInstance();
                    this.checklistListData.ShowCheckBox = false;
                    this.checklistListData.ShowDelete = false;
                    this.setTaggedItemsTab('checklist.module_name', ModuleType.CheckList);
                    break;
                case ModuleType.MediaBank:
                    this.setTaggedItemsTab('common.files', ModuleType.MediaBank);
                    break;
                case ModuleType.Core:
                    this.setTaggedItemsTab('common.our_organisation', ModuleType.Core);
                    break;
            }
        });
        this.cdr.detectChanges();
    }

    private setTaggedItemsTab(textId: string, ModuleType: ModuleType) {
        this.additionalTabs.push({
            Label: this.appTextService.get(textId),
            ModuleType,
        });
    }

    protected readonly FiDisplayType = FiDisplayType;
}
